import React, { forwardRef } from 'react';

/**
 * StyledCard - Componente card migliorato con supporto per ref e per tema chiaro/scuro
 */
const StyledCard = forwardRef(({ 
  children, 
  className = '', 
  variant = 'default', 
  hover = false,
  animate = false,
  ...props 
}, ref) => {
  // Determina le classi base in base alla variante - supporto per tema chiaro e scuro
  let variantClasses = '';
  
  switch (variant) {
    case 'primary':
      variantClasses = 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100';
      break;
    case 'success':
      variantClasses = 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-900 dark:text-green-100';
      break;
    case 'danger':
      variantClasses = 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-900 dark:text-red-100';
      break;
    case 'warning':
      variantClasses = 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100';
      break;
    case 'neutral':
      variantClasses = 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100';
      break;
    default:
      variantClasses = 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white';
  }
  
  // Aggiungi classi per hover effect se richiesto
  const hoverClasses = hover 
    ? 'hover:shadow-lg hover:-translate-y-1 transition-all duration-200' 
    : '';
    
  // Aggiungi classi per animazione se richiesto
  const animateClasses = animate 
    ? 'animate-fade-in' 
    : '';
    
  return (
    <div 
      ref={ref}
      className={`rounded-lg border shadow p-4 ${variantClasses} ${hoverClasses} ${animateClasses} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
});

// Aggiungi displayName per evitare warning in development
StyledCard.displayName = 'StyledCard';

// Subcomponenti per un'API più pulita
StyledCard.Header = ({ children, className = '' }) => (
  <div className={`mb-4 ${className}`}>
    {children}
  </div>
);

StyledCard.Title = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-900 dark:text-white ${className}`}>
    {children}
  </h3>
);

StyledCard.Subtitle = ({ children, className = '' }) => (
  <p className={`text-sm text-gray-600 dark:text-gray-300 ${className}`}>
    {children}
  </p>
);

StyledCard.Body = ({ children, className = '' }) => (
  <div className={className}>
    {children}
  </div>
);

StyledCard.Footer = ({ children, className = '' }) => (
  <div className={`mt-4 flex items-center ${className}`}>
    {children}
  </div>
);

StyledCard.Badge = ({ children, variant = 'default', className = '' }) => {
  let badgeClasses = '';
  
  switch (variant) {
    case 'primary':
      badgeClasses = 'bg-blue-100 dark:bg-blue-600/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700';
      break;
    case 'success':
      badgeClasses = 'bg-green-100 dark:bg-green-600/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      break;
    case 'danger':
      badgeClasses = 'bg-red-100 dark:bg-red-600/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700';
      break;
    case 'warning':
      badgeClasses = 'bg-amber-100 dark:bg-amber-600/30 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-700';
      break;
    default:
      badgeClasses = 'bg-gray-100 dark:bg-gray-600/30 text-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700';
  }
  
  return (
    <span className={`px-2 py-1 text-xs rounded-full border ${badgeClasses} ${className}`}>
      {children}
    </span>
  );
};

export default StyledCard;